import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  useOrganisationLabWorkbooks,
  usePatchLabWorkbookShippingSettings,
} from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface LabWorkbookShippingSettingsControlProps {
  workbookId: string;
  organisationId: number;
  isEnabled: boolean;
}

export function LabWorkbookShippingSettingsControl({
  workbookId,
  organisationId,
  isEnabled,
}: LabWorkbookShippingSettingsControlProps) {
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();
  const patchShippingSettings = usePatchLabWorkbookShippingSettings(workbookId);
  const shippingSettingsQuery = useOrganisationLabWorkbooks(organisationId, {
    enabled: isEnabled,
  });
  const workbook = useMemo(
    () => shippingSettingsQuery.data?.workbooks.find(candidate => candidate.id === workbookId) ?? null,
    [shippingSettingsQuery.data?.workbooks, workbookId],
  );
  const [availableValue, setAvailableValue] = useState('');
  const [quotaBeingSaved, setQuotaBeingSaved] = useState<number | null>(null);
  const [xlShipmentsAuthorised, setXlShipmentsAuthorised] = useState(false);
  const [isXlShipmentsAuthorisedBeingSaved, setIsXlShipmentsAuthorisedBeingSaved] = useState(false);

  useEffect(() => {
    if (!workbook) {
      return;
    }
    setAvailableValue(String(workbook.shippingRequestsAvailable));
    setXlShipmentsAuthorised(workbook.xlShipmentsAuthorised);
  }, [workbook]);

  const invalidateShippingQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['organisationLabWorkbooks', organisationId] }),
      queryClient.invalidateQueries({ queryKey: ['/shipping/workbooks'] }),
    ]);
  }, [organisationId, queryClient]);

  if (shippingSettingsQuery.isLoading) {
    return (
      <HStack spacing={2} color="gray.600">
        <Spinner size="sm" />
        <Text fontSize="sm">Loading shipping settings…</Text>
      </HStack>
    );
  }

  if (shippingSettingsQuery.error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription flex="1">Shipping settings could not be loaded.</AlertDescription>
        <Button size="sm" variant="outline" onClick={() => void shippingSettingsQuery.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!workbook) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription>Shipping settings are unavailable for this workbook.</AlertDescription>
      </Alert>
    );
  }

  const trimmedValue = availableValue.trim();
  const parsedAvailableValue = Number(trimmedValue);
  const allocatedQuota = workbook.shippingRequestsUsed + workbook.shippingRequestsPending;
  const maximumAvailableQuota = 1000 - allocatedQuota;
  const hasValidValue =
    trimmedValue !== '' &&
    Number.isInteger(parsedAvailableValue) &&
    parsedAvailableValue >= 0 &&
    parsedAvailableValue <= maximumAvailableQuota;
  const canSave = hasValidValue && parsedAvailableValue !== workbook.shippingRequestsAvailable;
  const workbookLabel = workbook.name?.trim() || workbook.sampleSheetName?.trim() || 'Untitled workbook';

  const saveAvailableQuota = async (nextAvailableQuota: number) => {
    const nextTotalQuota = allocatedQuota + nextAvailableQuota;
    setQuotaBeingSaved(nextAvailableQuota);
    try {
      await patchShippingSettings.mutateAsync({ shippingRequestQuota: nextTotalQuota });
      setAvailableValue(String(nextAvailableQuota));
      await invalidateShippingQueries();
      showToast({
        title: 'Shipping quota saved',
        description:
          nextAvailableQuota === 0
            ? `${workbookLabel} now has no available shipping requests.`
            : `${workbookLabel} now has ${nextAvailableQuota} available shipping request(s).`,
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      showToast({
        title: 'Shipping quota not saved',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setQuotaBeingSaved(null);
    }
  };

  const saveXlShipmentsAuthorised = async (nextValue: boolean) => {
    setXlShipmentsAuthorised(nextValue);
    setIsXlShipmentsAuthorisedBeingSaved(true);
    try {
      await patchShippingSettings.mutateAsync({ xlShipmentsAuthorised: nextValue });
      await invalidateShippingQueries();
      showToast({
        title: 'XL shipment authorisation saved',
        description: nextValue
          ? `${workbookLabel} can use XL shipments without the additional-charge warning.`
          : `${workbookLabel} will show the additional-charge warning for XL shipments.`,
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      setXlShipmentsAuthorised(workbook.xlShipmentsAuthorised);
      showToast({
        title: 'XL shipment authorisation not saved',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsXlShipmentsAuthorisedBeingSaved(false);
    }
  };

  const isSaving = patchShippingSettings.isPending;

  return (
    <Box>
      <Text fontWeight={600} mb={2} color="gray.700">
        Shipping requests
      </Text>
      <VStack align="stretch" spacing={3}>
        <HStack spacing={2} wrap="wrap">
          <Badge colorScheme={workbook.shippingRequestsAvailable > 0 ? 'green' : 'red'}>
            Available: {workbook.shippingRequestsAvailable}
          </Badge>
          <Badge colorScheme="blue">Pending: {workbook.shippingRequestsPending}</Badge>
          <Badge colorScheme="purple">Spent: {workbook.shippingRequestsUsed}</Badge>
        </HStack>
        <FormControl isInvalid={trimmedValue !== '' && !hasValidValue}>
          <FormLabel>Available shipping requests</FormLabel>
          <HStack align="start" spacing={2} wrap="wrap">
            <Input
              aria-label={`Available shipping requests for ${workbookLabel}`}
              size="sm"
              type="number"
              min={0}
              max={maximumAvailableQuota}
              step={1}
              value={availableValue}
              width="100px"
              bg="white"
              isDisabled={isSaving}
              onChange={event => {
                setAvailableValue(event.currentTarget.value);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              isDisabled={!canSave || isSaving}
              isLoading={quotaBeingSaved === parsedAvailableValue}
              onClick={() => {
                void saveAvailableQuota(parsedAvailableValue);
              }}
            >
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              colorScheme="red"
              isDisabled={workbook.shippingRequestsAvailable === 0 || isSaving}
              isLoading={quotaBeingSaved === 0}
              onClick={() => {
                void saveAvailableQuota(0);
              }}
            >
              Remove
            </Button>
          </HStack>
          {trimmedValue !== '' && !hasValidValue ? (
            <FormErrorMessage>Enter a whole number from 0 to {maximumAvailableQuota}.</FormErrorMessage>
          ) : null}
        </FormControl>
        <HStack spacing={2} wrap="wrap">
          <Checkbox
            isChecked={xlShipmentsAuthorised}
            isDisabled={isSaving}
            onChange={event => {
              void saveXlShipmentsAuthorised(event.currentTarget.checked);
            }}
          >
            XL shipments authorised
          </Checkbox>
          {isXlShipmentsAuthorisedBeingSaved ? <Spinner size="xs" /> : null}
        </HStack>
      </VStack>
    </Box>
  );
}
